<script>
// AgentSelector.vue
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';

export default {
  components: {
    NextButton,
    Avatar,
    Icon,
  },
  props: {
    agentList: {
      type: Array,
      default: () => [],
    },
    selectedAgents: {
      type: Array,
      default: () => [],
    },
    updateSelectedAgents: {
      type: Function,
      default: () => {},
    },
    isWorking: {
      type: Boolean,
      default: false,
    },
    submitButtonText: {
      type: String,
      default: '',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    hasInboxes: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    selectedAgentCount() {
      return this.selectedAgents.length;
    },
    allAgentsSelected() {
      // Lista vazia não é "todos selecionados": marcaria o checkbox sem nada
      // atrás dele.
      return (
        this.agentList.length > 0 &&
        this.selectedAgents.length === this.agentList.length
      );
    },
    disableSubmitButton() {
      return this.selectedAgentCount === 0 || !this.hasInboxes;
    },
  },
  methods: {
    isAgentSelected(agentId) {
      return this.selectedAgents.includes(agentId);
    },

    handleSelectAgent(agentId) {
      const shouldRemove = this.isAgentSelected(agentId);

      let result = [];
      if (shouldRemove) {
        result = this.selectedAgents.filter(item => item !== agentId);
      } else {
        result = [...this.selectedAgents, agentId];
      }

      this.updateSelectedAgents(result);
    },

    // Marca todos ou limpa a seleção, acompanhando o estado real do checkbox
    // do cabeçalho — antes ele só sabia marcar, então desmarcá-lo mentia.
    selectAllAgents() {
      const result = this.allAgentsSelected
        ? []
        : this.agentList.map(agent => agent.id);
      this.updateSelectedAgents(result);
    },

    // Aviso (não bloqueio): o agente já atende esta mesma caixa de entrada em
    // outro time, então vai entrar na fila de round-robin dos dois.
    overlapTooltip(agent) {
      return this.$t('TEAMS_SETTINGS.AGENTS.SHARED_INBOX_TOOLTIP', {
        teams: agent.overlappingTeams.join(', '),
      });
    },

    otherTeamsLabel(agent) {
      return this.$t('TEAMS_SETTINGS.AGENTS.ALSO_IN_TEAMS', {
        teams: agent.otherTeamNames.join(', '),
      });
    },

    agentRowClass(agent) {
      return this.isAgentSelected(agent.id) ? 'is-active' : '';
    },
  },
};
</script>

<template>
  <div>
    <!-- Skeleton Loader -->
    <div v-if="isLoading" class="space-y-0">
      <!-- Header Skeleton -->
      <div class="flex items-center gap-3 pb-3 border-b border-n-weak">
        <div class="h-4 w-4 bg-n-slate-3 rounded animate-pulse" />
        <div class="h-4 w-20 bg-n-slate-3 rounded animate-pulse" />
        <div class="flex-1" />
        <div class="h-4 w-32 bg-n-slate-3 rounded animate-pulse" />
        <div class="h-4 w-20 bg-n-slate-3 rounded animate-pulse" />
      </div>

      <!-- Rows Skeleton -->
      <div
        v-for="i in 5"
        :key="i"
        class="flex items-center gap-3 py-4 border-b border-n-weak"
      >
        <div class="h-4 w-4 bg-n-slate-3 rounded animate-pulse" />
        <div class="flex items-center gap-3 flex-1 min-w-0">
          <div
            class="size-6 bg-n-slate-3 rounded-full animate-pulse flex-shrink-0"
          />
          <div
            class="h-4 bg-n-slate-3 rounded animate-pulse"
            :style="{ width: `${100 + Math.random() * 100}px` }"
          />
        </div>
        <div
          class="h-4 bg-n-slate-3 rounded animate-pulse"
          :style="{ width: `${150 + Math.random() * 100}px` }"
        />
        <div class="flex items-center gap-2">
          <div class="size-4 bg-n-slate-3 rounded-full animate-pulse" />
          <div class="h-4 w-24 bg-n-slate-3 rounded animate-pulse" />
        </div>
      </div>

      <!-- Footer Skeleton -->
      <div
        class="flex items-center justify-between pt-4 border-t border-n-weak mt-4"
      >
        <div class="h-5 w-48 bg-n-slate-3 rounded animate-pulse" />
        <div class="h-10 w-36 bg-n-slate-3 rounded animate-pulse" />
      </div>
    </div>

    <!-- Tabela Real -->
    <table v-else class="w-full agent-selector-table">
      <thead
        class="[&>th]:font-semibold [&>th]:tracking-[1px] ltr:[&>th]:text-left rtl:[&>th]:text-right [&>th]:px-2.5 [&>th]:uppercase [&>th]:text-n-slate-12"
      >
        <tr>
          <th class="ltr:pl-2.5 rtl:pr-2.5 w-12">
            <div class="flex items-center">
              <input
                v-tooltip="$t('TEAMS_SETTINGS.AGENTS.SELECT_ALL')"
                name="select-all-agents"
                type="checkbox"
                :checked="allAgentsSelected"
                @click.self="selectAllAgents"
              />
            </div>
          </th>
          <th class="text-n-slate-12 ltr:pl-2.5 rtl:pr-2.5">
            {{ $t('TEAMS_SETTINGS.AGENTS.AGENT') }}
          </th>
          <th class="text-n-slate-12 ltr:pl-2.5 rtl:pr-2.5">
            {{ $t('TEAMS_SETTINGS.AGENTS.EMAIL') }}
          </th>
          <th class="text-n-slate-12 ltr:pl-2.5 rtl:pr-2.5">
            {{ $t('TEAMS_SETTINGS.AGENTS.STATUS') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="agent in agentList"
          :key="agent.id"
          :class="agentRowClass(agent)"
          class="border-b border-n-weak [&>td]:p-2.5 [&>td]:text-n-slate-12"
        >
          <td class="w-12">
            <div class="flex items-center">
              <input
                type="checkbox"
                :checked="isAgentSelected(agent.id)"
                @click.self="() => handleSelectAgent(agent.id)"
              />
            </div>
          </td>
          <td>
            <div class="flex items-center gap-2">
              <Avatar
                :src="agent.thumbnail"
                :name="agent.name"
                :status="agent.availability_status"
                :size="24"
                hide-offline-status
                rounded-full
              />
              <h4 class="text-base mb-0 text-n-slate-12">
                {{ agent.name }}
              </h4>
            </div>
          </td>
          <td>
            {{ agent.email || '---' }}
          </td>
          <td>
            <!-- Status do agente: informativo, nunca bloqueia a seleção -->
            <div class="flex flex-col gap-1">
              <div
                v-if="agent.isInCurrentTeam"
                class="flex items-center gap-2 text-n-green-11 text-sm"
              >
                <Icon icon="i-lucide-check-circle" />
                <span class="font-medium">
                  {{ $t('TEAMS_SETTINGS.AGENTS.IN_THIS_TEAM') }}
                </span>
              </div>
              <div
                v-else-if="agent.otherTeamNames.length"
                class="flex items-center gap-2 text-n-slate-11 text-sm"
              >
                <Icon icon="i-lucide-users" />
                <span>{{ otherTeamsLabel(agent) }}</span>
              </div>
              <div
                v-else
                class="flex items-center gap-2 text-n-slate-11 text-sm"
              >
                <Icon icon="i-lucide-circle" />
                <span>{{ $t('TEAMS_SETTINGS.AGENTS.AVAILABLE') }}</span>
              </div>

              <div
                v-if="agent.overlappingTeams.length"
                v-tooltip="overlapTooltip(agent)"
                class="flex items-center gap-2 text-n-amber-11 text-sm w-fit"
              >
                <Icon icon="i-lucide-alert-triangle" />
                <span class="font-medium">
                  {{ $t('TEAMS_SETTINGS.AGENTS.SHARED_INBOX') }}
                </span>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Summary and Submit -->
    <div
      class="flex items-center justify-between mt-4 pt-4 border-t border-n-weak"
    >
      <div class="flex flex-col gap-1">
        <p class="text-n-slate-12 font-medium">
          {{
            $t('TEAMS_SETTINGS.AGENTS.SELECTED_COUNT', {
              selected: selectedAgents.length,
              total: agentList.length,
            })
          }}
        </p>
      </div>
      <NextButton
        type="submit"
        :label="submitButtonText"
        :disabled="disableSubmitButton"
        :is-loading="isWorking"
      />
    </div>
  </div>
</template>

<style scoped>
input[type='checkbox'] {
  @apply mb-0;
}

table {
  @apply border-collapse;
}

.agent-selector-table tbody tr.is-active {
  @apply bg-n-blue-2;
}
</style>
